/**
 * Message writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const messageWritingFormatsPack: ToilePack = {
    slug: 'message-writing-formats',
    category: 'classroom',
    name: 'Message writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'phone', label: 'Phone message', subject: 'a boxed message with "MESSAGE", "Date", "Time", "Dear", "Body", "Name" labelled'},
        {key: 'family', label: 'To family', subject: 'a message format on a sticky note shape with the parts named'},
        {key: 'school', label: 'School message', subject: 'a message format with a 50-word limit note and the parts boxed'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Message Writing Format: Phone, Family, School',
        metaDescription: 'Message writing format on one labelled page: a phone message, a family message and a school message, in three styles.',
        h1: 'Message writing format: phone message, family message, school message',
        tagline: 'Nine labelled pages, three messages in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing the layout of a short message, with each part labelled: heading, date, time, greeting, body and name. The nine pages show a phone message, a message to family on a note-shaped page and a school message with a 50-word limit note. Ask for another message and the same labelled layout is asked for.',
        useCases: [
            'a classroom wall chart',
            'a reference for a short writing task',
            'a handout for a message writing exercise',
            'a sheet for a practice test',
        ],
        faq: [
            {q: 'Is there a word limit?', a: 'The school message page carries a note that sets a 50-word limit.'},
            {q: 'Does the page show a sample message?', a: 'No. The body is drawn as ruled lines, so the page shows the layout, not the wording.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for a message for another purpose?', a: 'Yes. Add a direction for another purpose and the labelled layout is asked for with it.'},
        ],
    },
}
